// src/context/AuthContext.js
// Updated: 02/13/2026 - Added Real-Time Remote Sync and Super Admin Fix

import React, { useContext, useState, useEffect, useCallback } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { auth, db } from '../services/firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore'; 
import { CircularProgress, Box } from '@mui/material';
import { initializeFCM } from '../services/pushNotificationService';

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [churchProfile, setChurchProfile] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Helper to safely trigger FCM logic
  // Added 'force' flag and removed strict 'church' requirement for Super Admins
  const refreshPushToken = useCallback((user, profile, church, force = false) => {
    if (user && profile && document.visibilityState === 'visible') {
      initializeFCM(user, profile, church, force).catch(err => 
        console.error("Auto-refresh of FCM token failed:", err)
      );
    }
  }, []);

  // 1. Listen for Auth Changes (Now using onSnapshot to enable remote token sync)
  useEffect(() => {
    let unsubscribeUser = () => {};

    const unsubscribeAuth = onIdTokenChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        await user.getIdTokenResult();

        const userDocRef = doc(db, 'users', user.uid);
        
        // Use real-time listener so the Admin Dashboard can remotely trigger syncs
        unsubscribeUser = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            setUserProfile(docSnap.data());
          } else {
            setUserProfile(null);
            console.log("No profile document found for user.");
          }
          setIsInitialized(true);
        });
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setChurchProfile(null);
        setIsInitialized(true);
        unsubscribeUser();
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeUser();
    };
  }, []);

  // 2. Listen for Real-time Church Data Updates
  useEffect(() => {
    if (userProfile?.churchId) {
      const churchDocRef = doc(db, 'churches', userProfile.churchId);

      const unsubscribeChurch = onSnapshot(churchDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const churchData = docSnap.data();
          setChurchProfile(churchData);
          // Trigger FCM init once we have church data (Force = true for initial load)
          refreshPushToken(currentUser, userProfile, churchData, true);
        } else {
          setChurchProfile(null);
          refreshPushToken(currentUser, userProfile, null, true); // Fallback for missing church doc
        }
      });

      return () => unsubscribeChurch();
    } else if (userProfile) {
      // Super Admin fallback (No churchId)
      setChurchProfile(null);
      refreshPushToken(currentUser, userProfile, null, true);
    } else {
      setChurchProfile(null);
    }
  }, [userProfile?.churchId, userProfile, currentUser, refreshPushToken]);

  // 3. AUTO-REFRESH: Trigger on Visibility Change AND Window Focus
  useEffect(() => {
    const handleActivity = () => {
      // Pass the current churchProfile state (Force = false to save DB writes)
      refreshPushToken(currentUser, userProfile, churchProfile, false);
    };

    document.addEventListener('visibilitychange', handleActivity);
    window.addEventListener('focus', handleActivity);

    return () => {
      document.removeEventListener('visibilitychange', handleActivity);
      window.removeEventListener('focus', handleActivity);
    };
  }, [currentUser, userProfile, churchProfile, refreshPushToken]);

  // 4. REMOTE SYNC LISTENER: Triggers when an Admin clicks "Force Sync"
  useEffect(() => {
    if (userProfile?.forceTokenSyncAt) {
      console.log("Remote sync signal received! Forcing FCM update...");
      refreshPushToken(currentUser, userProfile, churchProfile, true);
    }
  }, [userProfile?.forceTokenSyncAt, currentUser, churchProfile, refreshPushToken]);

  // 5. PERIODIC REFRESH: While app is open, refresh token every 6 hours
  useEffect(() => {
    if (!currentUser || !userProfile || !churchProfile) return;

    const interval = setInterval(() => {
      refreshPushToken(currentUser, userProfile, churchProfile, false);
    }, 6 * 60 * 60 * 1000); // 6 hours

    return () => clearInterval(interval);
  }, [currentUser, userProfile, churchProfile, refreshPushToken]);

  const value = {
    currentUser,
    userProfile,
    churchProfile,
    isInitialized,
  };

  if (!isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}